/**
 * YSTNJ Main Entry Point
 *
 * Configures AWS Amplify against YSTNJ's checked-in auth/data tuple.
 * Keep auth authority local to this app for the current Google auth unblock.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { Authenticator } from '@aws-amplify/ui-react';
import App from './App.tsx';
import { ensureAmplifyConfigured } from './amplifyClient';
import { ensureTriedgeBridgeGlobal } from '@triedge/bridge/global';
import { store } from './state/store';
import { ensureCryptoRandomUUIDSupport } from './runtime/ensureCryptoRandomUUID';
import { getRuntimeAmplifyOutputs } from './auth/oauthRuntimeConfig';
import { createYstnjParityBridge } from './parity/registerYstnjParityBridge';
import { initLogRocket } from './telemetry/logrocket';
import './tailwind.css';

ensureCryptoRandomUUIDSupport();
initLogRocket();

// Configure Amplify from the runtime authority tuple.
// The checked-in tuple tracks the active hosted target for this branch; set
// VITE_YSTNJ_AMPLIFY_OUTPUTS_PROFILE=workspace-local only for explicit sandbox work.
ensureAmplifyConfigured();
const runtimeOutputs = getRuntimeAmplifyOutputs();

// Protocol: internal app shell must always expose a configured window.TriedgeBridge.
// Do not add runtime bypass branches inside UI components; fix bootstrap instead.
ensureTriedgeBridgeGlobal({
  environment: import.meta.env.PROD ? 'production' : 'sandbox',
  region: runtimeOutputs.data.aws_region,
  userPoolId: runtimeOutputs.auth.user_pool_id,
  userPoolClientId: runtimeOutputs.auth.user_pool_client_id,
  graphqlEndpoint: runtimeOutputs.data.url,
})?.parity.register(createYstnjParityBridge());

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      {/* Auth context only: keep Amplify UI style layers out of composed route surfaces. */}
      <Authenticator.Provider>
        <App />
      </Authenticator.Provider>
    </Provider>
  </React.StrictMode>,
);
